import React, { useState, useEffect } from 'react';
import { Loader2, Database, Activity, TrendingUp, CheckCircle2 } from 'lucide-react';
import { useAnalysisContext } from '../../../../context/AnalysisContext';

/**
 * LoadingState Component - Shows progress during analysis execution
 *
 * Features:
 * - Animated spinner
 * - Status messages that rotate based on analysis stage
 * - Estimated time display
 * - Analysis type-specific messages
 */

interface StatusMessage {
  icon: React.ReactNode;
  message: string;
  duration: number; // milliseconds
}

export const LoadingState: React.FC = () => {
  const { selectedAnalysisType, selectedDisease } = useAnalysisContext();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Define status messages based on analysis type
  const getStatusMessages = (): StatusMessage[] => {
    const baseMessages: StatusMessage[] = [
      {
        icon: <Database className="w-5 h-5" />,
        message: 'Loading dataset from cloud storage...',
        duration: 5000,
      },
      {
        icon: <Activity className="w-5 h-5" />,
        message: 'Processing single-cell data...',
        duration: 8000,
      },
    ];

    switch (selectedAnalysisType) {
      case 'correlation':
        return [
          ...baseMessages,
          {
            icon: <TrendingUp className="w-5 h-5" />,
            message: 'Running UMAP dimensionality reduction...',
            duration: 10000,
          },
          {
            icon: <Activity className="w-5 h-5" />,
            message: 'Computing cell-type clusters...',
            duration: 8000,
          },
          {
            icon: <CheckCircle2 className="w-5 h-5" />,
            message: 'Identifying disease-specific lncRNAs...',
            duration: 7000,
          },
        ];

      case 'differential':
        return [
          ...baseMessages,
          {
            icon: <TrendingUp className="w-5 h-5" />,
            message: 'Computing differential expression...',
            duration: 12000,
          },
          {
            icon: <Activity className="w-5 h-5" />,
            message: 'Calculating fold-changes and p-values...',
            duration: 8000,
          },
          {
            icon: <CheckCircle2 className="w-5 h-5" />,
            message: 'Ranking candidate lncRNAs...',
            duration: 5000,
          },
        ];

      case 'grn':
        return [
          ...baseMessages,
          {
            icon: <TrendingUp className="w-5 h-5" />,
            message: 'Building gene regulatory network...',
            duration: 15000,
          },
          {
            icon: <Activity className="w-5 h-5" />,
            message: 'Computing correlation matrix...',
            duration: 10000,
          },
          {
            icon: <CheckCircle2 className="w-5 h-5" />,
            message: 'Identifying hub genes and interactions...',
            duration: 8000,
          },
        ];

      case 'stage':
        return [
          ...baseMessages,
          {
            icon: <TrendingUp className="w-5 h-5" />,
            message: 'Analyzing expression by disease stage...',
            duration: 12000,
          },
          {
            icon: <CheckCircle2 className="w-5 h-5" />,
            message: 'Identifying stage-specific targets...',
            duration: 8000,
          },
        ];

      default:
        return baseMessages;
    }
  };

  const statusMessages = getStatusMessages();
  const currentMessage = statusMessages[currentMessageIndex];

  // Rotate through status messages
  useEffect(() => {
    if (currentMessageIndex >= statusMessages.length - 1) {
      return; // Stay on last message
    }

    const timer = setTimeout(() => {
      setCurrentMessageIndex((prev) => Math.min(prev + 1, statusMessages.length - 1));
    }, currentMessage.duration);

    return () => clearTimeout(timer);
  }, [currentMessageIndex, statusMessages.length, currentMessage.duration]);

  // Track elapsed time
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Estimate total time based on analysis type
  const getEstimatedTime = (): number => {
    switch (selectedAnalysisType) {
      case 'correlation':
        return 40; // 40 seconds
      case 'differential':
        return 35; // 35 seconds
      case 'grn':
        return 50; // 50 seconds
      case 'stage':
        return 30; // 30 seconds
      default:
        return 30;
    }
  };

  const estimatedTime = getEstimatedTime();
  const progressPercent = Math.min((elapsedTime / estimatedTime) * 100, 95); // Cap at 95% until complete

  return (
    <div className="flex flex-col items-center justify-center min-h-[600px] px-6">
      {/* Spinner */}
      <div className="relative mb-8">
        <Loader2 className="w-16 h-16 text-[var(--origin-blue-500)] animate-spin" />

        {/* Progress ring */}
        <svg className="absolute inset-0 w-16 h-16 -rotate-90">
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="var(--border-primary)"
            strokeWidth="2"
          />
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="var(--origin-blue-500)"
            strokeWidth="2"
            strokeDasharray={`${2 * Math.PI * 28}`}
            strokeDashoffset={`${2 * Math.PI * 28 * (1 - progressPercent / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
        Running Analysis
      </h2>
      <p className="text-[var(--text-secondary)] mb-8">
        {selectedAnalysisType.charAt(0).toUpperCase() + selectedAnalysisType.slice(1)} analysis for {selectedDisease}
      </p>

      {/* Status Message */}
      <div className="origin-card p-6 max-w-md w-full mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="text-[var(--origin-blue-500)]">
            {currentMessage.icon}
          </div>
          <p className="text-[var(--text-primary)] font-medium">
            {currentMessage.message}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-[var(--bg-secondary)] rounded-full h-2 mb-3">
          <div
            className="bg-[var(--origin-blue-500)] h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Time Display */}
        <div className="flex items-center justify-between text-sm text-[var(--text-tertiary)]">
          <span>Elapsed: {elapsedTime}s</span>
          <span>Estimated: ~{estimatedTime}s</span>
        </div>
      </div>

      {/* Stage Indicators */}
      <div className="flex items-center gap-3">
        {statusMessages.map((msg, idx) => (
          <div
            key={idx}
            className={`
              w-3 h-3 rounded-full transition-all duration-300
              ${
                idx < currentMessageIndex
                  ? 'bg-[var(--success)]'
                  : idx === currentMessageIndex
                  ? 'bg-[var(--origin-blue-500)] animate-pulse'
                  : 'bg-[var(--border-primary)]'
              }
            `}
          />
        ))}
      </div>

      {/* Helper Text */}
      <p className="text-sm text-[var(--text-tertiary)] mt-8 max-w-md text-center">
        This may take up to {estimatedTime} seconds depending on dataset size.
        You can safely wait here - results will appear automatically.
      </p>
    </div>
  );
};

export default LoadingState;
